import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
    ArrowDownload16Regular,
    ChevronDown16Regular,
    Copy16Regular,
    DocumentAdd16Regular,
    MoreHorizontal16Regular,
    Sparkle16Regular,
} from '@fluentui/react-icons';
import { handleResponseAction } from '../../utils/responseActionHandlers';
import { isActionExecutable } from '../../utils/responseActionMeta';
import { resolveFollowUpQuestions } from '../../utils/responseActions';
import ResponseActionChip from './ResponseActionChip';
import styles from './askAi.module.css';

function FollowUpChip({ question, onSelect }) {
    return (
        <button
            type="button"
            className={styles.followUpChip}
            aria-label={`Ask follow-up: ${question}`}
            onClick={() => onSelect?.(question)}>
            <Sparkle16Regular aria-hidden />
            <span>{question}</span>
        </button>
    );
}

const ResponseActionsFooter = ({
    responseActions,
    askData = null,
    answerText = '',
    traceId = null,
    projectId = null,
    navigate,
    onCopy,
    onSaveJournal,
    onExportPdf,
    onViewEvidence,
    onOpenSource,
    onCreateObservation,
    onCreateTask,
    onCreateIrl,
    onCreateRaid,
    onCreateActionLog,
    onFollowUpQuestion,
}) => {
    const [moreOpen, setMoreOpen] = useState(false);
    const moreWrapRef = useRef(null);

    useEffect(() => {
        if (!moreOpen) return undefined;

        const handlePointerDown = event => {
            if (moreWrapRef.current && !moreWrapRef.current.contains(event.target)) {
                setMoreOpen(false);
            }
        };

        const handleEscape = event => {
            if (event.key === 'Escape') setMoreOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [moreOpen]);

    const handlers = useMemo(
        () => ({
            onViewEvidence,
            onOpenSource,
            onSaveJournal,
            onCreateObservation,
            onCreateTask,
            onCreateIrl,
            onCreateRaid,
            onCreateActionLog,
        }),
        [
            onViewEvidence,
            onOpenSource,
            onSaveJournal,
            onCreateObservation,
            onCreateTask,
            onCreateIrl,
            onCreateRaid,
            onCreateActionLog,
        ],
    );

    const followUpQuestions = useMemo(
        () => resolveFollowUpQuestions(askData || {}),
        [askData],
    );

    const runAction = (action, slot) => {
        handleResponseAction({
            action,
            slot,
            traceId: traceId || responseActions?.trace_id,
            projectId,
            navigate,
            handlers,
            answerText,
        });
    };

    if (!responseActions?.primary && !responseActions?.secondary && followUpQuestions.length === 0) {
        return null;
    }

    const utilities = responseActions?.utilities || ['copy', 'more'];
    const showCopy = utilities.includes('copy');
    const showMore = utilities.includes('more');

    const primaryDisabled = !isActionExecutable(responseActions?.primary, handlers);
    const secondaryDisabled = !isActionExecutable(responseActions?.secondary, handlers);

    return (
        <div className={styles.responseActionsFooter} role="group" aria-label="Response actions">
            {followUpQuestions.length > 0 ? (
                <div className={styles.followUpSection}>
                    <span className={styles.followUpLabel}>Suggested follow-ups</span>
                    <div className={styles.responseActionChipRow}>
                        {followUpQuestions.map(question => (
                            <FollowUpChip
                                key={question}
                                question={question}
                                onSelect={onFollowUpQuestion}
                            />
                        ))}
                    </div>
                </div>
            ) : null}

            <div className={styles.responseActionChipRow}>
                {responseActions?.primary ? (
                    <ResponseActionChip
                        action={responseActions.primary}
                        variant="primary"
                        disabled={primaryDisabled}
                        onClick={action => runAction(action, 'primary')}
                    />
                ) : null}
                {responseActions?.secondary ? (
                    <ResponseActionChip
                        action={responseActions.secondary}
                        variant="secondary"
                        disabled={secondaryDisabled}
                        onClick={action => runAction(action, 'secondary')}
                    />
                ) : null}

                {showCopy ? (
                    <button
                        type="button"
                        className={styles.responseActionChip}
                        aria-label="Copy answer"
                        onClick={() => onCopy?.(answerText)}>
                        <Copy16Regular aria-hidden />
                        <span className={styles.responseActionChipText}>
                            <span className={styles.responseActionChipLabel}>Copy</span>
                        </span>
                    </button>
                ) : null}

                {showMore ? (
                    <div className={styles.responseActionMoreWrap} ref={moreWrapRef}>
                        <button
                            type="button"
                            className={styles.responseActionChip}
                            aria-expanded={moreOpen}
                            aria-haspopup="menu"
                            aria-label="More actions"
                            onClick={() => setMoreOpen(open => !open)}>
                            <MoreHorizontal16Regular aria-hidden />
                            <span className={styles.responseActionChipText}>
                                <span className={styles.responseActionChipLabel}>More</span>
                            </span>
                            <ChevronDown16Regular aria-hidden />
                        </button>
                        {moreOpen ? (
                            <div className={styles.responseActionMoreMenu} role="menu">
                                <button
                                    type="button"
                                    className={styles.responseActionMoreItem}
                                    role="menuitem"
                                    onClick={() => {
                                        onSaveJournal?.(answerText);
                                        setMoreOpen(false);
                                    }}>
                                    <DocumentAdd16Regular aria-hidden /> Save to Journal
                                </button>
                                <button
                                    type="button"
                                    className={styles.responseActionMoreItem}
                                    role="menuitem"
                                    onClick={() => {
                                        onExportPdf?.(answerText);
                                        setMoreOpen(false);
                                    }}>
                                    <ArrowDownload16Regular aria-hidden /> Export PDF
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

function propsAreEqual(prev, next) {
    return (
        prev.answerText === next.answerText &&
        prev.traceId === next.traceId &&
        prev.projectId === next.projectId &&
        prev.responseActions === next.responseActions &&
        prev.askData === next.askData
    );
}

export default memo(ResponseActionsFooter, propsAreEqual);
